import type { ConceptDefinitionDTO, ParameterizedConceptDTO } from "@eu.mdotm.api/smart-workspaces";

/**
 * Concept catalog ids for the ex-ante concepts projected as columns when a forward-looking
 * scenario is selected in the Performance chart. Order here is the column order in the metrics
 * table.
 */
export const EX_ANTE_CONCEPT_IDS = ["systemId: exantereturn", "systemId: exantevolatility"] as const;

/** Same shape as `PortfolioMetricsConceptsConfig` (structural) — kept local to avoid a repository import cycle. */
export type ExAnteConceptsConfig = {
	conceptDefinitions: ConceptDefinitionDTO[];
	parametrizedConcepts: ParameterizedConceptDTO[];
};

/**
 * Picks the ex-ante concept definitions out of the full catalog and parametrizes each one
 * (CURRENT / ABSOLUTE / 3Y). Uuids are minted with `crypto.randomUUID()`, so the result is only
 * stable while it stays cached — the repository query pins it for the whole session (see
 * `exAnteScenarioConceptsConfig`). Definitions missing from the catalog are skipped.
 */
export function buildExAnteConceptsConfig(allDefinitions: ConceptDefinitionDTO[]): ExAnteConceptsConfig {
	const conceptDefinitions: ConceptDefinitionDTO[] = [];
	const parametrizedConcepts: ParameterizedConceptDTO[] = [];

	for (const conceptId of EX_ANTE_CONCEPT_IDS) {
		const definition = allDefinitions.find((d) => d.id === conceptId);
		if (!definition?.id) {
			continue;
		}
		conceptDefinitions.push(definition);
		parametrizedConcepts.push({
			uuid: crypto.randomUUID(),
			conceptId: definition.id,
			label: conceptIdLabel[definition.id as keyof typeof conceptIdLabel] ?? definition.defaultLabel ?? "",
			source: "CURRENT",
			representationMode: "ABSOLUTE",
			timeHorizon: "3Y",
			parameters: {},
		});
	}

	return { conceptDefinitions, parametrizedConcepts };
}

type ExAnteConcepts = (typeof EX_ANTE_CONCEPT_IDS)[number];
const conceptIdLabel: { [K in ExAnteConcepts]: string } = {
	"systemId: exantereturn": "Annualized returns",
	"systemId: exantevolatility": "Annualized volatility",
};
